import type { XPDistribution, ProgramModule, StepItem, ProgramDifficulty } from '../types';
import { calculateLevelBasedPoints, POINTS, PROGRAM_DIFFICULTY_MULTIPLIERS } from './levelCalculation';

export interface XPDistributionInput {
  moduleXP: number;
  performanceParameters: string[];
  muscleGroups: string[];
}

export function getDifficultyMultiplier(difficulty: ProgramDifficulty): number {
  // Config-hydrated (console XP economy → xp_rates); defaults match the
  // original beginner 1.0 → expert 2.0 ladder.
  return PROGRAM_DIFFICULTY_MULTIPLIERS[difficulty] ?? 1.0;
}

export function calculateScaledModuleXP(userLevel: number, difficulty: ProgramDifficulty): number {
  const baseXP = POINTS.PROGRAM_MODULE_BASE;
  const levelScaled = calculateLevelBasedPoints(baseXP, userLevel);
  const difficultyMultiplier = getDifficultyMultiplier(difficulty);
  return Math.round(levelScaled * difficultyMultiplier);
}

export function calculateScaledCompletionBonus(userLevel: number, difficulty: ProgramDifficulty): number {
  const baseBonus = POINTS.PROGRAM_COMPLETION_BONUS;
  const levelScaled = calculateLevelBasedPoints(baseBonus, userLevel);
  const difficultyMultiplier = getDifficultyMultiplier(difficulty);
  return Math.round(levelScaled * difficultyMultiplier);
}

export function calculateDurationBonus(
  totalDays: number,
  completionPercentage: number,
  userLevel: number
): number {
  const completionRatio = completionPercentage / 100;
  const baseBonus = totalDays * completionRatio * POINTS.PROGRAM_DURATION_BONUS_MULTIPLIER;
  return Math.round(calculateLevelBasedPoints(baseBonus, userLevel));
}

export function calculateScaledProgramTotalXP(
  moduleCount: number,
  durationDays: number,
  difficulty: ProgramDifficulty,
  userLevel: number
): { moduleXP: number; completionBonus: number; maxDurationBonus: number; total: number } {
  const moduleXP = calculateScaledModuleXP(userLevel, difficulty) * moduleCount;
  const completionBonus = calculateScaledCompletionBonus(userLevel, difficulty);
  const maxDurationBonus = calculateDurationBonus(durationDays, 100, userLevel);
  return {
    moduleXP,
    completionBonus,
    maxDurationBonus,
    total: moduleXP + completionBonus + maxDurationBonus
  };
}

export function calculateModuleXPDistribution(input: XPDistributionInput): XPDistribution {
  const { moduleXP, performanceParameters, muscleGroups } = input;

  const performanceParameterXP: Record<string, number> = {};
  const muscleGroupXP: Record<string, number> = {};

  if (performanceParameters.length > 0) {
    const xpPerParameter = moduleXP / performanceParameters.length;
    performanceParameters.forEach(param => {
      performanceParameterXP[param] = Math.round(xpPerParameter * 100) / 100;
    });
  }

  if (muscleGroups.length > 0) {
    const xpPerMuscleGroup = moduleXP / muscleGroups.length;
    muscleGroups.forEach(group => {
      muscleGroupXP[group] = Math.round(xpPerMuscleGroup * 100) / 100;
    });
  }

  return {
    totalXP: moduleXP,
    performanceParameterXP,
    muscleGroupXP,
    habitXP: [],
    domainXP: moduleXP,
    userXP: moduleXP
  };
}

export function extractMuscleGroupsFromSteps(steps: StepItem[]): string[] {
  const muscleGroups = new Set<string>();

  steps.forEach(step => {
    if (step.muscleGroups) {
      step.muscleGroups.forEach(group => muscleGroups.add(group));
    }
  });

  return Array.from(muscleGroups);
}

export function extractMuscleGroupsFromModule(module: ProgramModule): string[] {
  const muscleGroups = new Set<string>();

  if (module.muscleGroups) {
    module.muscleGroups.forEach(mapping => {
      muscleGroups.add(mapping.muscleGroup);
    });
  }

  module.contentBlocks.forEach(block => {
    if (block.type === 'steps_list' && block.metadata?.steps) {
      block.metadata.steps.forEach(step => {
        if (step.muscleGroups) {
          step.muscleGroups.forEach(group => muscleGroups.add(group));
        }
      });
    }
  });

  return Array.from(muscleGroups);
}

export function calculateModuleXPFromModule(module: ProgramModule): XPDistribution {
  const muscleGroups = extractMuscleGroupsFromModule(module);

  return calculateModuleXPDistribution({
    moduleXP: module.moduleXP || 100,
    performanceParameters: module.performanceParameters || [],
    muscleGroups
  });
}

export function calculateCompletedStepsXP(
  module: ProgramModule,
  completedStepIds: string[]
): XPDistribution {
  const allSteps: StepItem[] = [];
  const completedSteps: StepItem[] = [];

  module.contentBlocks.forEach(block => {
    if (block.type === 'steps_list' && block.metadata?.steps) {
      block.metadata.steps.forEach(step => {
        allSteps.push(step);
        if (completedStepIds.includes(step.id)) {
          completedSteps.push(step);
        }
      });
    }
  });

  if (allSteps.length === 0) {
    return calculateModuleXPFromModule(module);
  }

  const completionRatio = completedSteps.length / allSteps.length;
  const earnedXP = Math.round(module.moduleXP * completionRatio);

  const performanceParameterXP: Record<string, number> = {};
  const muscleGroupXP: Record<string, number> = {};

  if (module.performanceParameters.length > 0) {
    const xpPerParameter = earnedXP / module.performanceParameters.length;
    module.performanceParameters.forEach(param => {
      performanceParameterXP[param] = Math.round(xpPerParameter * 100) / 100;
    });
  }

  const completedMuscleGroups = extractMuscleGroupsFromSteps(completedSteps);
  if (completedMuscleGroups.length > 0) {
    const xpPerMuscleGroup = earnedXP / completedMuscleGroups.length;
    completedMuscleGroups.forEach(group => {
      muscleGroupXP[group] = Math.round(xpPerMuscleGroup * 100) / 100;
    });
  }

  return {
    totalXP: earnedXP,
    performanceParameterXP,
    muscleGroupXP,
    habitXP: [],
    domainXP: earnedXP,
    userXP: earnedXP
  };
}

export function calculateProgramTotalXP(modules: ProgramModule[]): number {
  return modules.reduce((total, module) => total + (module.moduleXP || 100), 0);
}

export function calculateCompletionStatus(
  completionPercentage: number
): 'completed' | 'partially_completed' {
  return completionPercentage >= 75 ? 'completed' : 'partially_completed';
}

export function mergeXPDistributions(distributions: XPDistribution[]): XPDistribution {
  const merged: XPDistribution = {
    totalXP: 0,
    performanceParameterXP: {},
    muscleGroupXP: {},
    habitXP: [],
    domainXP: 0,
    userXP: 0
  };

  distributions.forEach(dist => {
    merged.totalXP += dist.totalXP;
    merged.domainXP += dist.domainXP || 0;
    merged.userXP += dist.userXP || 0;

    Object.entries(dist.performanceParameterXP).forEach(([param, xp]) => {
      merged.performanceParameterXP[param] = (merged.performanceParameterXP[param] || 0) + xp;
    });

    Object.entries(dist.muscleGroupXP).forEach(([group, xp]) => {
      merged.muscleGroupXP[group] = (merged.muscleGroupXP[group] || 0) + xp;
    });

    if (dist.habitXP) {
      merged.habitXP.push(...dist.habitXP);
    }
  });

  return merged;
}
